import { ErrorReportingClient } from '../types/ErrorReportingClient';
import { ErrorReportingDevtools } from '../types/ErrorReportingDevtools';

const DEFAULT_SIMULATED_STATUS = 500;
const SERVER_ERROR_STATUS = 500;

/**
 * Build a synthetic HTTP client error that satisfies `beforeSend`'s HTTP error
 * detection (`config.url` + `response.status`) so QA can exercise
 * endpoint-specific grouping/fingerprinting without backend cooperation.
 */
const buildFakeAxiosError = (
  url: string,
  status: number,
  method: string,
): Error & Record<string, unknown> => {
  const error = new Error(
    `Simulated ${status} ${method.toUpperCase()} ${url}`,
  ) as Error & Record<string, unknown>;
  error.name = 'AxiosError';
  error.isAxiosError = true;
  error.code =
    status >= SERVER_ERROR_STATUS ? 'ERR_BAD_RESPONSE' : 'ERR_BAD_REQUEST';
  error.config = { method, url };
  error.response = {
    config: { method, url },
    data: { message: 'Simulated failure from errorReporting devtools' },
    headers: {},
    status,
    statusText: 'Simulated',
  };
  return error;
};

/**
 * Captures an exception shaped like an Axios error so the rest of the
 * pipeline (fingerprinting, ignore rules) behaves as it would for a real
 * network failure.
 */
export const createSimulateNetworkErrorDevtool = (
  client: ErrorReportingClient,
): ErrorReportingDevtools['simulateNetworkError'] => {
  return (url, status = DEFAULT_SIMULATED_STATUS, method = 'get'): void => {
    const error = buildFakeAxiosError(url, status, method);
    client.captureException(error, {
      extra: {
        simulated: true,
        source: 'errorReporting.simulateNetworkError',
      },
      tags: {
        simulated: 'true',
        simulatedMethod: method.toLowerCase(),
        simulatedStatus: String(status),
      },
    });
  };
};
